import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router';

interface SeoConfig {
	title: string;
	description: string;
	robots: string;
}

const DEFAULT_SITE_URL = 'https://build.w7s.io';
const DEFAULT_IMAGE_URL = 'https://github.com/w7s-io.png';
const JSON_LD_ID = 'w7s-build-seo-jsonld';

function getSiteUrl(): string {
	const configuredSiteUrl = import.meta.env.VITE_SITE_URL;
	if (
		typeof configuredSiteUrl === 'string' &&
		configuredSiteUrl.trim().length > 0
	) {
		return configuredSiteUrl.replace(/\/+$/, '');
	}

	if (typeof window !== 'undefined' && window.location.origin) {
		return window.location.origin.replace(/\/+$/, '');
	}

	return DEFAULT_SITE_URL;
}

function upsertMeta(
	attr: 'name' | 'property',
	key: string,
	content: string,
): void {
	const selector = `meta[${attr}="${key}"]`;
	let element = document.head.querySelector<HTMLMetaElement>(selector);

	if (!element) {
		element = document.createElement('meta');
		element.setAttribute(attr, key);
		document.head.appendChild(element);
	}

	element.setAttribute('content', content);
}

function upsertCanonical(href: string): void {
	let canonical = document.head.querySelector<HTMLLinkElement>(
		'link[rel="canonical"]',
	);
	if (!canonical) {
		canonical = document.createElement('link');
		canonical.setAttribute('rel', 'canonical');
		document.head.appendChild(canonical);
	}
	canonical.setAttribute('href', href);
}

function upsertJsonLd(data: Record<string, unknown> | null): void {
	const existing = document.head.querySelector<HTMLScriptElement>(
		`script#${JSON_LD_ID}`,
	);
	if (!data) {
		existing?.remove();
		return;
	}

	const script = existing ?? document.createElement('script');
	script.id = JSON_LD_ID;
	script.type = 'application/ld+json';
	script.textContent = JSON.stringify(data);
	if (!existing) {
		document.head.appendChild(script);
	}
}

function getSeoConfig(pathname: string): SeoConfig {
	if (pathname === '/') {
		return {
			title: 'W7S Build | Prompt to App. Push to Main.',
			description:
				'W7S Build turns product prompts into GitHub-native apps you can review, push, and ship.',
			robots: 'index, follow',
		};
	}

	if (pathname === '/discover') {
		return {
			title: 'Discover AI Apps | W7S Build',
			description:
				'Explore production-ready AI apps built with W7S Build. Browse real projects, patterns, and implementation quality.',
			robots: 'index, follow',
		};
	}

	if (pathname.startsWith('/app/')) {
		return {
			title: 'AI App Showcase | W7S Build',
			description:
				'Review an AI app built with W7S Build, including implementation details, deployment output, and source artifacts.',
			robots: 'index, follow',
		};
	}

	if (
		pathname.startsWith('/chat/') ||
		pathname === '/apps' ||
		pathname === '/settings' ||
		pathname === '/profile'
	) {
		return {
			title: 'W7S Build Workspace',
			description:
				'Secure W7S Build workspace for building and managing AI-powered applications.',
			robots: 'noindex, nofollow',
		};
	}

	return {
		title: 'W7S Build',
		description: 'W7S Build platform for building AI applications.',
		robots: 'noindex, nofollow',
	};
}

export function SeoManager() {
	const { pathname } = useLocation();

	const siteUrl = useMemo(() => getSiteUrl(), []);
	const config = useMemo(() => getSeoConfig(pathname), [pathname]);
	const canonicalUrl = `${siteUrl}${pathname === '/' ? '' : pathname}`;
	const imageUrl = DEFAULT_IMAGE_URL;

	useEffect(() => {
		document.title = config.title;
		document.documentElement.lang = 'en';

		upsertMeta('name', 'description', config.description);
		upsertMeta('name', 'robots', config.robots);
		upsertMeta('name', 'googlebot', config.robots);
		upsertMeta('name', 'twitter:card', 'summary_large_image');
		upsertMeta('name', 'twitter:title', config.title);
		upsertMeta('name', 'twitter:description', config.description);
		upsertMeta('name', 'twitter:image', imageUrl);

		upsertMeta('property', 'og:title', config.title);
		upsertMeta('property', 'og:description', config.description);
		upsertMeta('property', 'og:type', 'website');
		upsertMeta('property', 'og:url', canonicalUrl);
		upsertMeta('property', 'og:image', imageUrl);
		upsertMeta('property', 'og:site_name', 'W7S Build');

		upsertCanonical(canonicalUrl);

		upsertJsonLd({
			'@context': 'https://schema.org',
			'@graph': [
				{
					'@type': 'Organization',
					name: 'W7S',
					url: 'https://w7s.io',
					logo: imageUrl,
				},
				{
					'@type': 'WebSite',
					name: 'W7S Build',
					url: siteUrl,
					description: config.description,
				},
			],
		});
	}, [
		canonicalUrl,
		config.description,
		config.robots,
		config.title,
		imageUrl,
		siteUrl,
	]);

	return null;
}
